"use client";
import { useEffect, useState } from "react";
import { LogInIcon, LogOutIcon, MoreVerticalIcon, UserCircleIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { authClient } from "@/lib/authClient";
import { useRouter } from "next/navigation";
import { User } from "@/lib/types";
import { fetchUserCurrentEventBalance } from "@/query/einlass";

const fmtBalance = (n: number) => `${Number(n).toFixed(2).replace(".", ",")} €`;

// Sums balance across every currently active event. In practice events run
// sequentially (an old one gets deactivated before a new one sees any real
// activity), so a blended total is never actually ambiguous in the moment.
async function fetchTotalBalance(userId: string, eventIds: number[]): Promise<number> {
  const balances = await Promise.all(eventIds.map((eventId) => fetchUserCurrentEventBalance(userId, eventId)));
  return balances.reduce((sum, b) => sum + b, 0);
}

export function NavUser({ activeEventIds }: { activeEventIds: number[] }) {
  const { data: session } = authClient.useSession();
  const { isMobile } = useSidebar();
  const router = useRouter();
  const user = session?.user as User;
  // Show the nickname (mapped to `username` by the auth plugin), not the real name
  const displayName = (session?.user as { username?: string | null } | undefined)?.username ?? session?.user.name;

  const [balance, setBalance] = useState<number | null>(null);
  const [clanTag, setClanTag] = useState<string | null>(null);
  const activeEventIdsKey = activeEventIds.join(",");

  useEffect(() => {
    if (!user?.id || activeEventIds.length === 0) return;
    fetchTotalBalance(user.id, activeEventIds).then(setBalance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeEventIdsKey]);

  useEffect(() => {
    if (!user?.id || activeEventIds.length === 0) return;
    const refresh = () => fetchTotalBalance(user.id, activeEventIds).then(setBalance);
    window.addEventListener("balance-update", refresh);
    return () => window.removeEventListener("balance-update", refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeEventIdsKey]);

  if (!session) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            tooltip="Anmelden"
            className="group-data-[collapsible=icon]:justify-center border-[length:var(--btn-border-width)] border-t-[var(--btn-border-top)] border-l-[var(--btn-border-left)] border-b-[var(--btn-border-bottom)] border-r-[var(--btn-border-right)] shadow-[var(--btn-shadow)] bg-[var(--sidebar-group-bg)]"
            onClick={() => router.push("/login")}
          >
            <LogInIcon className="size-5 shrink-0" />
            <span className="font-medium group-data-[collapsible=icon]:hidden">Anmelden</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu className="rounded-xl border border-border bg-sidebar-primary-foreground">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip={displayName}
              className="border-[length:var(--btn-border-width)] border-t-[var(--btn-border-top)] border-l-[var(--btn-border-left)] border-b-[var(--btn-border-bottom)] border-r-[var(--btn-border-right)] shadow-[var(--btn-shadow)] bg-[var(--sidebar-group-bg)]  data-[state=open]: data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user?.image} alt={displayName} />
                <AvatarFallback className="rounded-lg">CN</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium inline-flex items-center gap-1.5">{displayName}</span>
                <span className="truncate text-xs text-muted-foreground">{session?.user.email}</span>
              </div>
              {balance !== null && <span className={`shrink-0 text-xs font-semibold tabular-nums ${balance >= 0 ? "text-green-600" : "text-red-500"}`}>{fmtBalance(balance)}</span>}
              <MoreVerticalIcon className="size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 rounded-lg" side={isMobile ? "bottom" : "right"} align="end" sideOffset={4}>
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user?.image} alt="" />
                  <AvatarFallback className="rounded-lg">CN</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium inline-flex items-center gap-1.5">{displayName}</span>
                  <span className="truncate text-xs text-muted-foreground">{session?.user.email}</span>
                </div>
                {balance !== null && <span className={`shrink-0 text-xs font-semibold tabular-nums ${balance >= 0 ? "text-green-600" : "text-red-500"}`}>{fmtBalance(balance)}</span>}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => router.push("/home/account")}>
                <UserCircleIcon />
                Account
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                await authClient.signOut({
                  fetchOptions: {
                    onSuccess: () => {
                      router.push("/home/news");
                      router.refresh();
                    },
                  },
                });
              }}
            >
              <LogOutIcon />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
